import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiThermometer, FiWind, FiSettings, FiTool, FiAlertTriangle, FiCheck } from 'react-icons/fi';
import SafeIcon from '../components/SafeIcon';
import PageHeader from '../components/shared/PageHeader';
import CtaSection from '../components/shared/CtaSection';

const Services = () => {
  const servicesList = [
    {
      id: "heating",
      icon: FiThermometer,
      title: "Heating Services",
      desc: "Comprehensive solutions for Montreal's harsh winters.",
      items: [
        "Furnace Installation & Replacement",
        "Furnace Repair & Diagnostics",
        "Boiler Services",
        "Heat Pump Installation",
        "Annual Heating Tune-ups"
      ],
      benefits: "Lower energy bills, reliable warmth, and extended equipment lifespan.",
      timeline: "Repairs: Same day | Installs: 1-2 days"
    },
    {
      id: "cooling",
      icon: FiWind,
      title: "Air Conditioning",
      desc: "Stay comfortable during humid summer heatwaves.",
      items: [
        "Central AC Installation",
        "AC Repair & Troubleshooting",
        "Ductless Mini-Split Systems",
        "Refrigerant Recharging",
        "Spring AC Maintenance"
      ],
      benefits: "Improved indoor air quality, consistent cooling, reduced humidity.",
      timeline: "Repairs: Same day | Installs: 1 day"
    },
    {
      id: "emergency",
      icon: FiAlertTriangle,
      title: "24/7 Emergency Repairs",
      desc: "Fast response when you need it most, day or night.",
      items: [
        "No Heat Situations",
        "AC Breakdowns in Extreme Heat",
        "Strange Noises or Odors",
        "Leaking Systems",
        "Thermostat Failures"
      ],
      benefits: "Immediate peace of mind, prevention of property damage, restored safety.",
      timeline: "Response: Under 2 hours"
    },
    {
      id: "ventilation",
      icon: FiSettings,
      title: "Ventilation & IAQ",
      desc: "Breathe easier with advanced indoor air quality solutions.",
      items: [
        "Air Purifiers & Scrubbers",
        "Humidifiers & Dehumidifiers",
        "HRV/ERV Systems",
        "Duct Cleaning & Sealing",
        "UV Germicidal Lights"
      ],
      benefits: "Reduced allergens, balanced humidity, healthier living environment.",
      timeline: "Varies by system type"
    }
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <PageHeader 
        title="Our HVAC Services" 
        subtitle="Expert heating, cooling, and ventilation solutions tailored for Greater Montreal's unique climate."
        image="https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?auto=format&fit=crop&q=80&w=2000"
      />

      <section className="section-padding bg-bg">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {servicesList.map((service, index) => (
              <motion.div 
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden flex flex-col"
              >
                <div className={`p-8 border-b border-gray-100 ${service.id === 'emergency' ? 'bg-red-50/50' : 'bg-blue-50/30'}`}>
                  <div className="flex items-center gap-4 mb-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${service.id === 'emergency' ? 'bg-red-100 text-red-600' : 'bg-primary/10 text-primary'}`}>
                      <SafeIcon icon={service.icon} className="text-2xl" />
                    </div>
                    <h2 className="text-2xl font-bold">{service.title}</h2>
                  </div>
                  <p className="text-muted text-lg">{service.desc}</p>
                </div>
                
                <div className="p-8 flex-grow flex flex-col">
                  <h4 className="font-bold mb-4 uppercase text-sm tracking-wider text-gray-500">What We Do</h4>
                  <ul className="space-y-3 mb-8 flex-grow">
                    {service.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <SafeIcon icon={FiCheck} className="text-green-500 mt-1 flex-shrink-0" />
                        <span className="text-textMain font-medium">{item}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="bg-gray-50 rounded-xl p-5 mb-8">
                    <div className="mb-3">
                      <span className="font-bold text-sm text-gray-700">Benefits:</span>
                      <p className="text-sm text-muted mt-1">{service.benefits}</p>
                    </div>
                    <div>
                      <span className="font-bold text-sm text-gray-700">Typical Timeline:</span>
                      <p className="text-sm text-muted mt-1">{service.timeline}</p>
                    </div>
                  </div>

                  <Link to="/quote" className={`w-full text-center py-3 rounded-lg font-bold transition-colors ${
                    service.id === 'emergency' 
                      ? 'bg-accent text-white hover:bg-accentHover' 
                      : 'bg-primary text-white hover:bg-secondary'
                  }`}>
                    {service.id === 'emergency' ? 'Request Emergency Service' : 'Get a Quote'}
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <CtaSection />
    </div>
  );
};

export default Services;